import { useEffect } from 'react'
import { fetchThreadMessages, isThreadsApiAvailable } from '@/services/chat/threadsApi'
import { applyFetchedThreadMessages } from './applyFetchedThreadMessages'
import { useLiveWorkStatus } from './useLiveWorkStatus'

/**
 * How often the open thread is re-read. A scheduled run writes its message from
 * the scheduler container, so nothing in this tab ever hears about it: there is
 * no socket and no realtime subscription, and `useBackendThreadMessages` fetches
 * a thread once. Without this poll the message exists in the database and the
 * open tab shows the thread exactly as it was.
 */
const POLL_MS = 20000

/** Re-read the open thread so messages written by a scheduled run appear. */
export function usePollThreadMessages(model) {
  const { displayThreadId, setMessagesByThreadId } = model
  useLiveWorkStatus(displayThreadId, setMessagesByThreadId)

  useEffect(() => {
    const id = displayThreadId
    if (!id || !isThreadsApiAvailable()) return undefined
    let controller = null
    const timer = window.setInterval(() => {
      // A background tab has nobody watching it; polling it only spends quota.
      if (typeof document !== 'undefined' && document.hidden) return
      controller?.abort()
      controller = new AbortController()
      fetchThreadMessages(id, { signal: controller.signal })
        .then((messages) => {
          // `unresolved: false` keeps the existing merge rules, which refuse to
          // overwrite a reply that is still streaming in this tab.
          setMessagesByThreadId((prev) => applyFetchedThreadMessages(prev, id, messages, false))
        })
        .catch(() => {})
    }, POLL_MS)
    return () => {
      window.clearInterval(timer)
      controller?.abort()
    }
  }, [displayThreadId, setMessagesByThreadId])

}
